import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';

import InboundCarriers from '@src/components/logistics/import/inbound-carriers';
import InboundCarrier from '@src/components/logistics/import/inbound-carrier';
import ImportConsignments from '@src/components/logistics/import/consignments/consignments';
import ImportConsignment from '@src/components/logistics/import/consignments/consignment';
import ConsignmentPreloading from '@src/components/logistics/import/consignments/consignment-preloading';
import ImportBills from '@src/components/logistics/import/bills/bills';
import DeliveryTrucks from '@src/components/logistics/delivery/trucks';
import DeliveryTruck from '@src/components/logistics/delivery/truck';
import Remainings from '@src/components/warehouse/remainings/remainings';
import Remaining from '@src/components/warehouse/remainings/remaining';
import Tags from '@src/components/tags/tags';
import Tag from '@src/components/tags/tag';
import Consignees from '@src/components/consignees/consignees';
import Consignee from '@src/components/consignees/consignee';
import Tariffs from '@src/components/tariffs/tariffs';
import Tariff from '@src/components/tariffs/tariff';
import BillHeaders from '@src/components/bills/bill-headers';
import Users from '@src/components/users/users';
import User from '@src/components/users/user';
import Contacts from '@src/components/contacts/contacts';
import Contact from '@src/components/contacts/contact';
import Orders from '@src/components/orders/orders';
import Order from '@src/components/orders/order';
import Documents from '@src/components/documents/documents';
import Accounts from '@src/components/accounts/accounts';
import Account from '@src/components/accounts/account';
import Counterparties from '@src/components/counterparties/counterparties';
import UserAccount from '@src/components/user-account/user-account';
import AuditLogs from '@src/components/audit-logs/audit-logs';
import Balances from '@src/components/balances/balances';
import BalanceDetails from '@src/components/balances/balance-details';
import CargoCarriers from '@src/components/cargo-carriers/cargo-carriers';
import CargoCarrier from '@src/components/cargo-carriers/cargo-carrier';
import Items from '@src/components/items/items';
import Item from '@src/components/items/item';
import Countries from '@src/components/countries/countries';
import Country from '@src/components/countries/country';
import Preloading from '@src/components/logistics/import/preloading/preloading';
import Acceptance from '@src/components/warehouse/acceptance/acceptance';
import WarehouseTruck from '@src/components/warehouse/acceptance/warehouse-carrier';
import WarehouseConsignment from '@src/components/warehouse/acceptance/warehouse-consignment';
import ShipmentJournals from '@src/components/warehouse/shipment-journals/shipment-journals';
import ShipmentJournal from '@src/components/warehouse/shipment-journals/shipment-journal';
import Cities from '@src/components/cities/cities';

import Companies from '@components/companies/companies';
import Company from '@components/companies/company';
import Performers from '@components/performers/performers';
import NewNotification from '@components/notifications/new-notification';
import Emails from '@components/notifications/emails';
import PushMessages from '@components/notifications/push-mesages';
import TelegramMessages from '@components/notifications/telegram-messages';
import SmsMessages from '@components/notifications/sms-messages';

import UsersStatistic from '@components/statistics/users-statistic';

import { ProtectedRoute } from '@controls/protected-route';

import MainLayout from '@src/main-layout';

import { IUserSession } from '@src/entities/user-session';

import { Permission, hasPermission } from '@src/enums/permission';

import { serverFetch } from '@src/server';

interface IAppRoutes {
    userSession: IUserSession;
    appVersion: string | undefined;
}

const AppRoutes = (props: IAppRoutes) => {
    const { userSession, appVersion } = props;

    return (
        <Routes>
            <Route path='/' element={<MainLayout appVersion={appVersion} />}>
                <Route index element={<Navigate replace to={`/logistics/import`} />} />

                <Route path='logistics/preloading' element={<Preloading />} />
                <Route path='logistics/import' element={<InboundCarriers />} />
                <Route path='logistics/import/new' element={<InboundCarrier />} />
                <Route path='logistics/import/:id' element={<InboundCarrier />} />
                <Route path='logistics/import/consignments/:carrierId' element={<ImportConsignments />} />
                <Route path='logistics/import/consignments/new/:carrierId' element={<ImportConsignment />} />
                <Route path='logistics/import/consignments/preloading/:carrierId' element={<ConsignmentPreloading />} />
                <Route
                    path='logistics/import/consignments/:carrierId/:consignmentId'
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageInboundCarrierConsignment) ||
                                hasPermission(userSession.permissions, Permission.ViewInboundCarrierConsignment)
                            }
                            redirectPath={'/'}
                            children={<ImportConsignment />}
                        />
                    }
                />
                <Route
                    path='logistics/import/tariffs/:carrierId'
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
                                hasPermission(userSession.permissions, Permission.EditTransportBill) ||
                                hasPermission(userSession.permissions, Permission.ViewTransportBill)
                            }
                            redirectPath={'/'}
                            children={<ImportBills />}
                        />
                    }
                />
                <Route path='logistics/delivery' element={<DeliveryTrucks />} />
                <Route path='logistics/delivery/new/:journalId' element={<DeliveryTruck />} />
                <Route path='logistics/delivery/:truckId/:journalId?' element={<DeliveryTruck />} />
                <Route path='warehouse/acceptance' element={<Acceptance serverFetch={serverFetch} />} />
                <Route path='warehouse/acceptance/:id' element={<WarehouseTruck serverFetch={serverFetch} />} />
                <Route
                    path='warehouse/acceptance/consignments/:carrierId/:consignmentId'
                    element={<WarehouseConsignment serverFetch={serverFetch} />}
                />
                <Route path='warehouse/remainings' element={<Remainings />} />
                <Route path='warehouse/remainings/:boxGroupId' element={<Remaining />} />
                <Route path='warehouse/remainings/shipment/:journalId?' element={<Remainings />} />
                <Route
                    path='warehouse/extraconsignments/consignments/:consignmentId'
                    element={<WarehouseConsignment serverFetch={serverFetch} />}
                />
                <Route path='warehouse/shipmentjournals' element={<ShipmentJournals serverFetch={serverFetch} />} />
                <Route path='warehouse/shipmentjournals/:id' element={<ShipmentJournal serverFetch={serverFetch} />} />
                <Route path='warehouse/shipmentjournals/:journalId/truck/new' element={<DeliveryTruck />} />
                <Route path='warehouse/shipmentjournals/:journalId/truck/:truckId' element={<DeliveryTruck />} />

                <Route path='warehouse/performers/:areaType/:sourceType/:сarrierId' element={<Performers serverFetch={serverFetch} />} />
                <Route
                    element={
                        <ProtectedRoute isAllowed={hasPermission(userSession.permissions, Permission.ManageTariff)} redirectPath={'/'} />
                    }
                >
                    <Route path='finance/tariffs' element={<Tariffs />} />
                    <Route path='finance/tariffs/new/:headerId' element={<Tariff />} />
                    <Route path='finance/tariffs/:id/:isCopy?' element={<Tariff />} />
                </Route>
                <Route
                    path='finance/bills/:userId?'
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageTransportBill) ||
                                hasPermission(userSession.permissions, Permission.EditTransportBill) ||
                                hasPermission(userSession.permissions, Permission.ViewTransportBill) ||
                                hasPermission(userSession.permissions, Permission.SetPaid)
                            }
                            redirectPath={'/'}
                            children={<BillHeaders />}
                        />
                    }
                />
                <Route
                    element={
                        <ProtectedRoute
                            isAllowed={
                                hasPermission(userSession.permissions, Permission.ManageBalance) ||
                                hasPermission(userSession.permissions, Permission.ViewBalance)
                            }
                            redirectPath={'/'}
                        />
                    }
                >
                    <Route path='finance/balances' element={<Balances />} />
                    <Route path='finance/balances/:userId' element={<BalanceDetails />} />
                </Route>

                <Route
                    path='notifications/newnotification'
                    element={<NewNotification serverFetch={serverFetch} modulePath='flp' serviceName='Посадка' />}
                />
                <Route path='notifications/sms' element={<SmsMessages serverFetch={serverFetch} />} />
                <Route path='notifications/emails' element={<Emails serverFetch={serverFetch} />} />
                <Route path='notifications/push' element={<PushMessages serverFetch={serverFetch} />} />
                <Route path='notifications/telegram' element={<TelegramMessages serverFetch={serverFetch} />} />

                <Route path='orders/:userId' element={<Orders />} />
                <Route path='orders/:userId/:orderId' element={<Order />} />

                <Route path='documents' element={<Documents />} />

                <Route
                    element={
                        <ProtectedRoute
                            isAllowed={hasPermission(userSession.permissions, Permission.ViewUsersStatistic)}
                            redirectPath={'/'}
                        />
                    }
                >
                    <Route path='statistics/usersstatistic' element={<UsersStatistic serverFetch={serverFetch} />} />
                </Route>

                <Route path='settings/countries' element={<Countries serverFetch={serverFetch} />} />
                <Route path='settings/countries/new' element={<Country serverFetch={serverFetch} />} />
                <Route path='settings/countries/:id' element={<Country serverFetch={serverFetch} />} />
                <Route path='settings/cities' element={<Cities serverFetch={serverFetch} />} />
                <Route path='settings/consignees' element={<Consignees />} />
                <Route path='settings/consignees/new' element={<Consignee />} />
                <Route path='settings/consignees/:id' element={<Consignee />} />
                <Route path='settings/tags' element={<Tags />} />
                <Route path='settings/tags/new' element={<Tag />} />
                <Route path='settings/users' element={<Users />} />
                <Route path='settings/users/new' element={<User />} />
                <Route path='settings/users/:id' element={<User />} />
                <Route path='settings/counterparties' element={<Counterparties />} />
                <Route path='settings/contacts' element={<Contacts />} />
                <Route path='settings/contacts/new' element={<Contact />} />
                <Route path='settings/contacts/:id' element={<Contact />} />
                <Route path='settings/accounts' element={<Accounts />} />
                <Route path='settings/accounts/new' element={<Account />} />
                <Route path='settings/accounts/:id' element={<Account />} />
                <Route path='settings/items' element={<Items serverFetch={serverFetch} />} />
                <Route path='settings/items/new' element={<Item serverFetch={serverFetch} />} />
                <Route path='settings/items/:id' element={<Item serverFetch={serverFetch} />} />
                <Route path='settings/companies' element={<Companies serverFetch={serverFetch} />} />
                <Route path='settings/companies/new' element={<Company serverFetch={serverFetch} />} />
                <Route path='settings/companies/:id' element={<Company serverFetch={serverFetch} />} />
                <Route path='settings/cargocarriers' element={<CargoCarriers />} />
                <Route path='settings/cargocarriers/new' element={<CargoCarrier />} />
                <Route path='settings/cargocarriers/:id' element={<CargoCarrier />} />
                <Route path='useraccount' element={<UserAccount />} />
                <Route path='auditlogs' element={<AuditLogs />} />
            </Route>
        </Routes>
    );
};

export default AppRoutes;
